import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, onDocumentReady } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * Predictive search component with fallback to full search results
 * Ensures products, articles, and pages (including images) are always shown.
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();
  #activeFetch = null;

  #resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      this.style.setProperty('--predictive-search-results-height', `${entry.contentRect.height}px`);
    }
  });

  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();
    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      this.addEventListener('click', this.#handleModalClick, { signal });
    } else {
      document.addEventListener(ThemeEvents.megaMenuHover, this.#blurSearch, { signal });
    }

    onDocumentReady(this.#getRecentlyViewed);

    const results = this.refs.predictiveSearchResults.firstElementChild;
    if (results) this.#resizeObserver.observe(results);
  }

  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractive =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractive && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#resizeObserver.disconnect();
  }

  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  #handleDialogClose = () => {
    this.#resetSearch();
  };

  expandSearch = () => {
    this.classList.add('predictive-search--expanded');
    if (this.dataset.activeColorScheme) {
      const target = this.dialog ?? this;
      target.classList.add(`color-${this.dataset.activeColorScheme}`);
    }
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    return containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(
          container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card')
        );
      })
      .filter((item) => item instanceof HTMLElement);
  }

  #isKeyboardNavigation = false;

  get #currentIndex() {
    return (
      this.#allResultsItems?.findIndex(
        (item) => item.getAttribute('aria-selected') === 'true'
      ) ?? -1
    );
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => item.classList.remove('keyboard-focus'));

    for (const [i, item] of this.#allResultsItems.entries()) {
      if (i === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) item.classList.add('keyboard-focus');
        item.scrollIntoView({
          behavior: prefersReducedMotion() ? 'instant' : 'smooth',
          block: 'nearest'
        });
      } else {
        item.removeAttribute('aria-selected');
      }
    }
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const current = this.#currentIndex;
    const total = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = current < total - 1 ? current + 1 : 0;
        break;
      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = current > 0 ? current - 1 : total - 1;
        break;
      case 'Tab':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = event.shiftKey
          ? current > 0
            ? current - 1
            : total - 1
          : current < total - 1
          ? current + 1
          : 0;
        break;
      case 'Enter':
        const single = this.refs.predictiveSearchResults.querySelector(
          '[data-single-result-url]'
        );
        if (single instanceof HTMLElement && single.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = single.dataset.singleResultUrl;
          return;
        }
        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
    }
  };

  clearRecentlyViewedProducts(event) {
    event.stopPropagation();
    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;
    const elements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];
    if (!elements.length) return;

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');
      onAnimationEnd(recentlyViewedWrapper, () => recentlyViewedWrapper.remove());
    }
  }

  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) this.refs.searchInput.focus();
    this.#resetSearch();
  }, 100);

  search = debounce((event) => {
    if (!event.inputType) return;
    const term = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (term.length < 1) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(term);
  }, 200);

  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const inner = this.refs.predictiveSearchResults.querySelector(
        '.predictive-search-results__inner'
      );
      if (inner instanceof HTMLElement) inner.scrollTop = 0;

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) formContent.scrollTop = 0;
    });
  }

  /**
   * Predictive search with full search fallback.
   */
  async #getSearchResults(term) {
    const predictiveUrl = new URL(Theme.routes.predictive_search_url, location.origin);
    predictiveUrl.searchParams.set('q', term);
    predictiveUrl.searchParams.set('resources[type]', 'product,article,page');
    predictiveUrl.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;
    const controller = this.#createAbortController();

    // 1. Try predictive search
    const predictiveMarkup = await sectionRenderer.getSectionHTML(
      this.dataset.sectionId,
      false,
      predictiveUrl
    );
    if (controller.signal.aborted) return;

    const doc = new DOMParser().parseFromString(predictiveMarkup || '', 'text/html');
    const foundCards = doc.querySelectorAll('.predictive-search-results__card').length;

    // 2. If predictive search fails, fallback to full search results
    if (!predictiveMarkup || !predictiveMarkup.trim() || foundCards === 0) {
      const fullUrl = new URL(Theme.routes.search_url, location.origin);
      fullUrl.searchParams.set('q', term);
      fullUrl.searchParams.set('resources[type]', 'product,article,page');

      const fallbackMarkup = await sectionRenderer.getSectionHTML(
        this.dataset.sectionId,
        false,
        fullUrl
      );
      if (!controller.signal.aborted && fallbackMarkup) {
        morph(predictiveSearchResults, fallbackMarkup);
        this.#resetScrollPositions();
      }
      return;
    }

    // Otherwise, show predictive search results
    morph(predictiveSearchResults, predictiveMarkup);
    this.#resetScrollPositions();
  }

  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewed = RecentlyViewed.getProducts();
    if (!viewed.length) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewed.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #getRecentlyViewed = async () => {
    const { predictiveSearchResults } = this.refs;
    const controller = this.#createAbortController();

    const markup = await this.#getRecentlyViewedProductsMarkup();
    if (!markup || controller.signal.aborted) return;

    const doc = new DOMParser().parseFromString(markup, 'text/html');
    const html = doc.getElementById('predictive-search-products');
    if (!html) return;

    for (const child of html.children) {
      if (child instanceof HTMLElement) child.setAttribute('ref', 'recentlyViewedWrapper');
    }

    const collection = predictiveSearchResults.querySelector('#predictive-search-products');
    if (!collection) return;

    if (this.refs.recentlyViewedWrapper) this.refs.recentlyViewedWrapper.remove();
    collection.prepend(...html.children);
  };

  #hideResetButton() {
    this.refs.resetButton.hidden = true;
  }

  #showResetButton() {
    this.refs.resetButton.hidden = false;
  }

  #createAbortController() {
    const c = new AbortController();
    if (this.#activeFetch) this.#activeFetch.abort();
    this.#activeFetch = c;
    return c;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const controller = this.#createAbortController();
    const emptyId = 'predictive-search-empty';
    const markup = await sectionRenderer.getSectionHTML(emptyId, false);
    const parsed = new DOMParser()
      .parseFromString(markup, 'text/html')
      .querySelector('.predictive-search-empty-section');
    if (!parsed) return;

    const viewed = RecentlyViewed.getProducts();
    if (viewed.length > 0) {
      const rv = await this.#getRecentlyViewedProductsMarkup();
      if (rv) {
        const parsedRV = new DOMParser().parseFromString(rv, 'text/html');
        const html = parsedRV.getElementById('predictive-search-products');
        if (html) {
          for (const child of html.children) {
            if (child instanceof HTMLElement) child.setAttribute('ref', 'recentlyViewedWrapper');
          }
          const collection = parsed.querySelector('#predictive-search-products');
          if (collection) collection.prepend(...html.children);
        }
      }
    }

    if (!controller.signal.aborted) {
      morph(predictiveSearchResults, parsed);
      this.#resetScrollPositions();
    }
  };

  #blurSearch = () => {
    this.refs.searchInput.blur();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
